import { useCallback, useEffect, useRef, useState } from "react";
import { Send, Users, Crown } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n/LanguageContext";
import { api, errMsg } from "@/lib/api";

const Avatar = ({ name, picture, size = "w-9 h-9" }) =>
  picture ? (
    <img src={picture} alt="" className={`${size} rounded-full object-cover shrink-0`} referrerPolicy="no-referrer" />
  ) : (
    <span className={`${size} shrink-0 grid place-items-center rounded-full bg-gradient-to-br from-sky-400 to-fuchsia-500 text-sm font-semibold text-[#050713]`}>{name?.[0]?.toUpperCase()}</span>
  );

const time = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export const Chat = ({ user }) => {
  const { t } = useLang();
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [text, setText] = useState("");
  const listRef = useRef(null);
  const lastRef = useRef(null);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/messages", { params: lastRef.current ? { after: lastRef.current } : {} });
      if (Array.isArray(data) && data.length) {
        lastRef.current = data[data.length - 1].created_at;
        setMessages((m) => {
          const ids = new Set(m.map((x) => x.message_id));
          return [...m, ...data.filter((x) => !ids.has(x.message_id))];
        });
      }
    } catch { /* retry next tick */ }
  }, []);

  useEffect(() => {
    poll();
    api.get("/chat/members").then(({ data }) => setMembers(Array.isArray(data) ? data : [])).catch(() => {});
    const id = setInterval(poll, 3000);
    return () => clearInterval(id);
  }, [poll]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setText("");
    try {
      await api.post("/chat/messages", { text: body });
      poll();
    } catch (err) {
      toast.error(errMsg(err, t.common.error));
      setText(body);
    }
  };

  return (
    <div className="w-full max-w-6xl grid md:grid-cols-[1fr_260px] gap-4 h-[calc(100svh-112px)]" data-testid="members-chat">
      <section className="glass rounded-[1.75rem] flex flex-col min-h-0 overflow-hidden">
        <header className="px-5 md:px-7 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-extrabold text-white">{t.chat.title}</h1>
            <p className="text-sm text-slate-400">{t.chat.sub}</p>
          </div>
          <span className="md:hidden flex items-center gap-1.5 text-sm text-slate-300"><Users className="w-4 h-4" /> {members.length}</span>
        </header>
        <div ref={listRef} data-lenis-prevent className="flex-1 overflow-y-auto px-4 md:px-7 py-6 space-y-4" data-testid="chat-message-list">
          {messages.length === 0 && <p className="text-center text-slate-400 mt-10" data-testid="chat-empty">{t.chat.empty}</p>}
          {messages.map((m) => {
            const mine = m.user_id === user.user_id;
            return (
              <div key={m.message_id} className={`flex gap-3 items-end msg-in ${mine ? "flex-row-reverse" : ""}`} data-testid="chat-message">
                <Avatar name={m.name} picture={m.picture} size="w-8 h-8" />
                <div className={`max-w-[78%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                  <p className="text-xs text-slate-400 mb-1 flex items-center gap-1">
                    {m.role === "admin" && <Crown className="w-3 h-3 text-amber-300" />}
                    {mine ? t.chat.you : m.name} · {time(m.created_at)}
                  </p>
                  <p className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words ${mine ? "bg-gradient-to-br from-sky-400 to-violet-500 text-[#050713] rounded-br-sm" : "bg-white/10 text-slate-100 rounded-bl-sm"}`}>{m.text}</p>
                </div>
              </div>
            );
          })}
        </div>
        <form onSubmit={send} className="p-3 md:p-4 border-t border-white/10 flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder={t.chat.placeholder} className="field flex-1" data-testid="chat-message-input" />
          <button className="btn-chrome rounded-full px-4 md:px-5 flex items-center gap-2 font-semibold text-[#050713]" data-testid="chat-send-button" aria-label={t.chat.send}>
            <Send className="w-4 h-4" /> <span className="hidden sm:inline">{t.chat.send}</span>
          </button>
        </form>
      </section>
      <aside className="hidden md:flex glass rounded-[1.75rem] p-5 flex-col min-h-0" data-testid="chat-members-sidebar">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-sky-300">{t.chat.members} · {members.length}</p>
        <ul className="mt-5 space-y-3 overflow-y-auto" data-lenis-prevent>
          {members.map((m) => (
            <li key={m.user_id} className="flex items-center gap-3">
              <span className="relative"><Avatar name={m.name} picture={m.picture} size="w-8 h-8" /><span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-[#0b0e26]" /></span>
              <span className="text-sm text-slate-200 truncate">{m.name}</span>
              {m.role === "admin" && <span className="ml-auto text-[11px] font-mono uppercase text-amber-300">{t.chat.owner}</span>}
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
};
